import React, { useState } from 'react';
import { Row, Col, Card, Table, Badge, Form, Button } from 'react-bootstrap';
import { 
  BarChart, Bar, PieChart, Pie, LineChart, Line, XAxis, YAxis, 
  CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, AreaChart, Area
} from 'recharts';
import { FaFileDownload, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';

// Utils
const formatCurrency = (value) => {
  if (value === null || value === undefined) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(value);
};

const formatNumber = (value, decimals = 0) => {
  if (value === null || value === undefined) return '0';
  return value.toLocaleString('en-US', { 
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const getStatusBadgeVariant = (status) => {
  switch (status) {
    case 'Active':
      return 'success';
    case 'Inactive':
      return 'secondary';
    case 'New':
      return 'primary';
    case 'On Hold':
      return 'warning';
    default:
      return 'secondary';
  }
};

// Color palette for charts
const COLORS = [
  '#3366CC', '#DC3912', '#FF9900', '#109618', '#990099', 
  '#0099C6', '#DD4477', '#66AA00', '#B82E2E', '#316395'
];

const ClientsReport = ({ data, viewMode = 'both' }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [searchTerm, setSearchTerm] = useState('');
  
  if (!data || !data.data) {
    return <div>No data available</div>;
  }
  
  const reportType = data.type;
  let reportData = data.data;
  
  // Calculate summary statistics
  const calculateSummary = () => {
    if (reportType === 'client-activity') {
      const totalClients = reportData.summary?.totalClients || 0;
      const activeClients = reportData.summary?.activeClients || 0;
      
      return {
        totalClients,
        activeClients,
        activeRate: totalClients > 0 ? activeClients / totalClients : 0,
        newClients: reportData.summary?.newClients || 0
      };
    } else if (reportType === 'client-projects') {
      const totalProjects = reportData.summary?.totalProjects || 0;
      const totalClients = reportData.summary?.totalClients || 0;
      
      return {
        totalProjects,
        totalClients,
        avgProjectsPerClient: totalClients > 0 ? totalProjects / totalClients : 0,
        completedProjects: reportData.summary?.completedProjects || 0
      };
    } else if (reportType === 'client-revenue') {
      const totalRevenue = reportData.summary?.totalRevenue || 0;
      const totalClients = reportData.summary?.totalClients || 0;
      
      return {
        totalRevenue,
        totalClients,
        avgRevenuePerClient: totalClients > 0 ? totalRevenue / totalClients : 0,
        topClientRevenue: reportData.summary?.topClientRevenue || 0
      };
    } else if (reportType === 'year-over-year') {
      const currentYearRevenue = reportData.summary?.currentYearRevenue || 0;
      const previousYearRevenue = reportData.summary?.previousYearRevenue || 0;
      const growthRate = previousYearRevenue > 0 ? 
        (currentYearRevenue - previousYearRevenue) / previousYearRevenue : 0;
      
      return {
        currentYearRevenue,
        previousYearRevenue,
        growthRate,
        totalClients: reportData.summary?.totalClients || 0
      };
    }
    
    return {};
  };
  
  const summary = calculateSummary();
  
  // Sort table data
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };
  
  const getSortIcon = (columnName) => {
    if (sortConfig.key !== columnName) {
      return <FaSort className="ms-1 text-muted" />;
    }
    return sortConfig.direction === 'ascending' ? 
      <FaSortUp className="ms-1" /> : <FaSortDown className="ms-1" />;
  };
  
  const getSortedData = (items) => {
    if (!items || !sortConfig.key) return items;
    
    const sortableItems = [...items];
    sortableItems.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      // Handle null values
      if (aValue === null) return sortConfig.direction === 'ascending' ? -1 : 1;
      if (bValue === null) return sortConfig.direction === 'ascending' ? 1 : -1;
      
      // For strings, ignore case
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      // Dates need special handling
      if (sortConfig.key === 'LastActivityDate' || sortConfig.key === 'CreatedAt') {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      }
      
      if (aValue < bValue) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
    
    return sortableItems;
  };
  
  // Filter table data
  const getFilteredData = (items) => {
    if (!items || !searchTerm) return items;
    
    return items.filter(item => {
      // Search through all string and number fields
      return Object.keys(item).some(key => {
        const value = item[key];
        if (value === null || value === undefined) return false;
        
        if (typeof value === 'string') {
          return value.toLowerCase().includes(searchTerm.toLowerCase());
        } else if (typeof value === 'number') {
          return value.toString().includes(searchTerm);
        }
        
        return false;
      });
    });
  };
  
  // Render client activity report
  const renderClientActivity = () => {
    // Filter and sort clients
    let clients = reportData.clients || [];
    if (searchTerm) {
      clients = getFilteredData(clients);
    }
    if (sortConfig.key) {
      clients = getSortedData(clients);
    }
    
    // Client status chart
    const clientStatus = reportData.byStatus || [];
    
    // Activity by month chart
    const activityByMonth = reportData.byMonth || [];
    
    const renderCharts = () => (
      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Body>
              <h5 className="text-center mb-3">Client Status Distribution</h5>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={clientStatus}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    fill="#8884d8"
                    paddingAngle={5}
                    dataKey="value"
                    nameKey="name"
                    label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {clientStatus.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value} Clients`, 'Count']} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6}>
          <Card>
            <Card.Body>
              <h5 className="text-center mb-3">Client Activity Over Time</h5>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={activityByMonth}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Line 
                    yAxisId="left"
                    type="monotone" 
                    dataKey="activities" 
                    name="Activities" 
                    stroke="#8884d8" 
                    strokeWidth={2}
                  />
                  <Line 
                    yAxisId="right"
                    type="monotone" 
                    dataKey="newClients" 
                    name="New Clients" 
                    stroke="#82ca9d" 
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
    
    const renderClientsTable = () => (
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Client Activity Details</h5>
            <div className="d-flex gap-2">
              <Form.Control
                type="text"
                placeholder="Search clients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="sm"
                style={{ width: '200px' }}
              />
              <Button 
                variant="outline-secondary" 
                size="sm"
                title="Export as CSV"
              >
                <FaFileDownload />
              </Button>
            </div>
          </div>
          
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th onClick={() => requestSort('ClientName')} className="sortable">
                  Client Name {getSortIcon('ClientName')}
                </th>
                <th onClick={() => requestSort('ContactName')} className="sortable">
                  Contact {getSortIcon('ContactName')}
                </th>
                <th onClick={() => requestSort('LastActivityDate')} className="sortable">
                  Last Activity {getSortIcon('LastActivityDate')}
                </th>
                <th onClick={() => requestSort('ActivityCount')} className="sortable">
                  Activity Count {getSortIcon('ActivityCount')}
                </th>
                <th onClick={() => requestSort('ProjectCount')} className="sortable">
                  Projects {getSortIcon('ProjectCount')}
                </th>
                <th onClick={() => requestSort('Status')} className="sortable">
                  Status {getSortIcon('Status')}
                </th>
              </tr>
            </thead>
            <tbody>
              {clients.map((client, index) => (
                <tr key={index}>
                  <td>{client.ClientName}</td>
                  <td>{client.ContactName}</td>
                  <td>{formatDate(client.LastActivityDate)}</td>
                  <td>{client.ActivityCount}</td>
                  <td>{client.ProjectCount}</td>
                  <td>
                    <Badge bg={getStatusBadgeVariant(client.Status)}>
                      {client.Status}
                    </Badge>
                  </td>
                </tr>
              ))}
              {clients.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center">No client activity data found</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    );
    
    return (
      <>
        <div className="summary-cards">
          <div className="summary-card">
            <h5>Total Clients</h5>
            <h3>{summary.totalClients}</h3>
          </div>
          <div className="summary-card">
            <h5>Active Clients</h5>
            <h3>{summary.activeClients}</h3>
          </div>
          <div className="summary-card">
            <h5>Active Rate</h5>
            <h3>{(summary.activeRate * 100).toFixed(1)}%</h3>
          </div>
          <div className="summary-card">
            <h5>New Clients</h5>
            <h3>{summary.newClients}</h3>
          </div>
        </div>
        
        {(viewMode === 'chart' || viewMode === 'both') && renderCharts()}
        {(viewMode === 'table' || viewMode === 'both') && renderClientsTable()}
      </>
    );
  };
  
  // Render client projects report
  const renderClientProjects = () => {
    // Filter and sort client projects
    let clientProjects = reportData.clientProjects || [];
    if (searchTerm) {
      clientProjects = getFilteredData(clientProjects);
    }
    if (sortConfig.key) {
      clientProjects = getSortedData(clientProjects);
    }
    
    // Projects by client chart
    const projectsByClient = reportData.byClient || [];
    
    // Projects by status chart
    const projectsByStatus = reportData.byStatus || [];
    
    const renderCharts = () => (
      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Body>
              <h5 className="text-center mb-3">Projects by Client (Top 10)</h5>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={projectsByClient.slice(0, 10)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip formatter={(value) => [`${value} Projects`, 'Count']} />
                  <Legend />
                  <Bar dataKey="count" name="Project Count" fill="#8884d8">
                    {projectsByClient.slice(0, 10).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6}>
          <Card>
            <Card.Body>
              <h5 className="text-center mb-3">Projects by Status</h5>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={projectsByStatus}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    fill="#8884d8"
                    paddingAngle={5}
                    dataKey="value"
                    nameKey="name"
                    label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {projectsByStatus.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value} Projects`, 'Count']} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
    
    const renderProjectsTable = () => (
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Client Projects Details</h5>
            <div className="d-flex gap-2">
              <Form.Control
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="sm"
                style={{ width: '200px' }}
              />
              <Button 
                variant="outline-secondary" 
                size="sm"
                title="Export as CSV"
              >
                <FaFileDownload />
              </Button>
            </div>
          </div>
          
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th onClick={() => requestSort('ClientName')} className="sortable">
                  Client {getSortIcon('ClientName')}
                </th>
                <th onClick={() => requestSort('ProjectName')} className="sortable">
                  Project {getSortIcon('ProjectName')}
                </th>
                <th onClick={() => requestSort('Status')} className="sortable">
                  Status {getSortIcon('Status')}
                </th>
                <th onClick={() => requestSort('CreatedAt')} className="sortable">
                  Start Date {getSortIcon('CreatedAt')}
                </th>
                <th onClick={() => requestSort('CompletionDate')} className="sortable">
                  Completion Date {getSortIcon('CompletionDate')}
                </th>
                <th onClick={() => requestSort('Value')} className="sortable">
                  Value {getSortIcon('Value')}
                </th>
              </tr>
            </thead>
            <tbody>
              {clientProjects.map((project, index) => (
                <tr key={index}>
                  <td>{project.ClientName}</td>
                  <td>{project.ProjectName}</td>
                  <td>
                    <Badge bg={getStatusBadgeVariant(project.Status)}>
                      {project.Status}
                    </Badge>
                  </td>
                  <td>{formatDate(project.CreatedAt)}</td>
                  <td>{project.CompletionDate ? formatDate(project.CompletionDate) : 'N/A'}</td>
                  <td>{formatCurrency(project.Value)}</td>
                </tr>
              ))}
              {clientProjects.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center">No client projects found</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    );
    
    return (
      <>
        <div className="summary-cards">
          <div className="summary-card">
            <h5>Total Projects</h5>
            <h3>{summary.totalProjects}</h3>
          </div>
          <div className="summary-card">
            <h5>Total Clients</h5>
            <h3>{summary.totalClients}</h3>
          </div>
          <div className="summary-card">
            <h5>Avg Projects/Client</h5>
            <h3>{formatNumber(summary.avgProjectsPerClient, 1)}</h3>
          </div>
          <div className="summary-card">
            <h5>Completed Projects</h5>
            <h3>{summary.completedProjects}</h3>
          </div>
        </div>
        
        {(viewMode === 'chart' || viewMode === 'both') && renderCharts()}
        {(viewMode === 'table' || viewMode === 'both') && renderProjectsTable()}
      </>
    );
  };
  
  // Render client revenue report
  const renderClientRevenue = () => {
    // Filter and sort client revenue
    let clientRevenue = reportData.revenue || [];
    if (searchTerm) {
      clientRevenue = getFilteredData(clientRevenue);
    }
    if (sortConfig.key) {
      clientRevenue = getSortedData(clientRevenue);
    }
    
    // Revenue by client chart
    const revenueByClient = reportData.byClient || [];
    
    // Monthly revenue trend chart
    const revenueByMonth = reportData.byMonth || [];
    
    const renderCharts = () => (
      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Body>
              <h5 className="text-center mb-3">Revenue by Client (Top 10)</h5>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={revenueByClient.slice(0, 10)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis 
                    tickFormatter={(value) => `$${Math.floor(value / 1000)}k`}
                  />
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), 'Revenue']}
                  />
                  <Legend />
                  <Bar dataKey="value" name="Revenue" fill="#8884d8">
                    {revenueByClient.slice(0, 10).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6}>
          <Card>
            <Card.Body>
              <h5 className="text-center mb-3">Revenue Trend</h5>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={revenueByMonth}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis 
                    tickFormatter={(value) => `$${Math.floor(value / 1000)}k`}
                  />
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), 'Revenue']}
                  />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="value" 
                    name="Revenue" 
                    fill="#8884d8" 
                    stroke="#8884d8"
                    fillOpacity={0.6}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
    
    const renderRevenueTable = () => (
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Client Revenue Details</h5>
            <div className="d-flex gap-2">
              <Form.Control
                type="text"
                placeholder="Search clients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="sm"
                style={{ width: '200px' }}
              />
              <Button 
                variant="outline-secondary" 
                size="sm"
                title="Export as CSV"
              >
                <FaFileDownload />
              </Button>
            </div>
          </div>
          
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th onClick={() => requestSort('ClientName')} className="sortable">
                  Client {getSortIcon('ClientName')}
                </th>
                <th onClick={() => requestSort('ProjectCount')} className="sortable">
                  Projects {getSortIcon('ProjectCount')}
                </th>
                <th onClick={() => requestSort('TotalRevenue')} className="sortable">
                  Total Revenue {getSortIcon('TotalRevenue')}
                </th>
                <th onClick={() => requestSort('AvgRevenue')} className="sortable">
                  Avg Revenue {getSortIcon('AvgRevenue')}
                </th>
                <th onClick={() => requestSort('LastPurchase')} className="sortable">
                  Last Purchase {getSortIcon('LastPurchase')}
                </th>
                <th onClick={() => requestSort('RevenuePercentage')} className="sortable">
                  % of Total {getSortIcon('RevenuePercentage')}
                </th>
              </tr>
            </thead>
            <tbody>
              {clientRevenue.map((client, index) => (
                <tr key={index}>
                  <td>{client.ClientName}</td>
                  <td>{client.ProjectCount}</td>
                  <td>{formatCurrency(client.TotalRevenue)}</td>
                  <td>{formatCurrency(client.AvgRevenue)}</td>
                  <td>{formatDate(client.LastPurchase)}</td>
                  <td>{(client.RevenuePercentage * 100).toFixed(1)}%</td>
                </tr>
              ))}
              {clientRevenue.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center">No client revenue data found</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    );
    
    return (
      <>
        <div className="summary-cards">
          <div className="summary-card">
            <h5>Total Revenue</h5>
            <h3>{formatCurrency(summary.totalRevenue)}</h3>
          </div>
          <div className="summary-card">
            <h5>Total Clients</h5>
            <h3>{summary.totalClients}</h3>
          </div>
          <div className="summary-card">
            <h5>Avg Revenue/Client</h5>
            <h3>{formatCurrency(summary.avgRevenuePerClient)}</h3>
          </div>
          <div className="summary-card">
            <h5>Top Client Revenue</h5>
            <h3>{formatCurrency(summary.topClientRevenue)}</h3>
          </div>
        </div>
        
        {(viewMode === 'chart' || viewMode === 'both') && renderCharts()}
        {(viewMode === 'table' || viewMode === 'both') && renderRevenueTable()}
      </>
    );
  };
  
  // Render year-over-year analysis report
  const renderYearOverYear = () => {
    // Filter and sort client data
    let clientYearOverYear = reportData.clients || [];
    if (searchTerm) {
      clientYearOverYear = getFilteredData(clientYearOverYear);
    }
    if (sortConfig.key) {
      clientYearOverYear = getSortedData(clientYearOverYear);
    }
    
    // Year over year revenue chart
    const yearOverYearData = reportData.yearOverYear || [];
    
    // Top growing clients chart
    const topGrowingClients = reportData.topGrowing || [];
    
    const renderCharts = () => (
      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Body>
              <h5 className="text-center mb-3">Year Over Year Revenue</h5>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={yearOverYearData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis 
                    tickFormatter={(value) => `$${Math.floor(value / 1000)}k`}
                  />
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), '']}
                  />
                  <Legend />
                  <Bar dataKey="current" name="Current Year" fill="#8884d8" />
                  <Bar dataKey="previous" name="Previous Year" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6}>
          <Card>
            <Card.Body>
              <h5 className="text-center mb-3">Top Growing Clients</h5>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={topGrowingClients} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" 
                    tickFormatter={(value) => `${(value * 100).toFixed(0)}%`}
                  />
                  <YAxis type="category" dataKey="name" width={120} />
                  <Tooltip 
                    formatter={(value) => [`${(value * 100).toFixed(1)}%`, 'Growth Rate']}
                  />
                  <Legend />
                  <Bar dataKey="growth" name="Growth Rate" fill="#82ca9d">
                    {topGrowingClients.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry.growth >= 0 ? '#82ca9d' : '#ff8042'}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
    
    const renderYearOverYearTable = () => (
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Client Year Over Year Comparison</h5>
            <div className="d-flex gap-2">
              <Form.Control
                type="text"
                placeholder="Search clients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="sm"
                style={{ width: '200px' }}
              />
              <Button 
                variant="outline-secondary" 
                size="sm"
                title="Export as CSV"
              >
                <FaFileDownload />
              </Button>
            </div>
          </div>
          
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th onClick={() => requestSort('ClientName')} className="sortable">
                  Client {getSortIcon('ClientName')}
                </th>
                <th onClick={() => requestSort('CurrentYearRevenue')} className="sortable">
                  Current Year {getSortIcon('CurrentYearRevenue')}
                </th>
                <th onClick={() => requestSort('PreviousYearRevenue')} className="sortable">
                  Previous Year {getSortIcon('PreviousYearRevenue')}
                </th>
                <th onClick={() => requestSort('Change')} className="sortable">
                  Change {getSortIcon('Change')}
                </th>
                <th onClick={() => requestSort('GrowthRate')} className="sortable">
                  Growth Rate {getSortIcon('GrowthRate')}
                </th>
                <th onClick={() => requestSort('ProjectCount')} className="sortable">
                  Projects {getSortIcon('ProjectCount')}
                </th>
              </tr>
            </thead>
            <tbody>
              {clientYearOverYear.map((client, index) => {
                const growthRate = client.PreviousYearRevenue !== 0 ? 
                  (client.CurrentYearRevenue - client.PreviousYearRevenue) / client.PreviousYearRevenue : 
                  client.CurrentYearRevenue > 0 ? 1 : 0;
                  
                return (
                  <tr key={index}>
                    <td>{client.ClientName}</td>
                    <td>{formatCurrency(client.CurrentYearRevenue)}</td>
                    <td>{formatCurrency(client.PreviousYearRevenue)}</td>
                    <td className={client.Change >= 0 ? 'text-success' : 'text-danger'}>
                      {formatCurrency(client.Change)}
                    </td>
                    <td className={client.GrowthRate >= 0 ? 'text-success' : 'text-danger'}>
                      {(client.GrowthRate * 100).toFixed(1)}%
                    </td>
                    <td>{client.ProjectCount}</td>
                  </tr>
                );
              })}
              {clientYearOverYear.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center">No year over year data found</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    );
    
    return (
      <>
        <div className="summary-cards">
          <div className="summary-card">
            <h5>Current Year</h5>
            <h3>{formatCurrency(summary.currentYearRevenue)}</h3>
          </div>
          <div className="summary-card">
            <h5>Previous Year</h5>
            <h3>{formatCurrency(summary.previousYearRevenue)}</h3>
          </div>
          <div className="summary-card">
            <h5>Growth Rate</h5>
            <h3 className={summary.growthRate >= 0 ? 'text-success' : 'text-danger'}>
              {(summary.growthRate * 100).toFixed(1)}%
            </h3>
          </div>
          <div className="summary-card">
            <h5>Total Clients</h5>
            <h3>{summary.totalClients}</h3>
          </div>
        </div>
        
        {(viewMode === 'chart' || viewMode === 'both') && renderCharts()}
        {(viewMode === 'table' || viewMode === 'both') && renderYearOverYearTable()}
      </>
    );
  };
  
  // Render the appropriate report based on the report type
  switch (reportType) {
    case 'client-activity':
      return renderClientActivity();
    case 'client-projects':
      return renderClientProjects();
    case 'client-revenue':
      return renderClientRevenue();
    case 'year-over-year':
      return renderYearOverYear();
    default:
      return <div>Unknown report type</div>;
  }
};

export default ClientsReport;